import { Col, Row } from 'react-grid-system'
import cx from 'classnames'
import styles from './CardGroup.module.scss'
import { getResponsiveCarouselSettings } from 'utils/carousel/getResponsiveCarouselSettings'
import { useWindowSize } from 'utils/hooks'
import { Card, CarouselV2, Heading } from '~elements'
import { CardGroupType } from '~types'
import { useMemo } from 'react'
import { normalizeDataToCard } from 'utils/transform/normalizeDataToCard'

const CardGroup = ({
  backgroundGradient,
  backgroundColor,
  cards,
  cardsHaveLargeFontSizes = false,
  centerCopyOnMobile,
  hasDescriptionListCheckMark,
  headline,
  isCarousel,
  isCarouselMobileOnly,
  stackMediaAndCopy = true,
  subheadlineColor,
  slidesToShow: rawSlidesToShow,
  hasFilters = false,
  dataOptions,
  enableModelSelection = false
}: CardGroupType) => {
  const slidesToShow = rawSlidesToShow ?? 3
  const { isBreakpoint: isBelowSm } = useWindowSize('sm', '<')
  const { isBreakpoint: isBelowMd } = useWindowSize('md', '<')
  const cardCount = cards?.length ?? 0
  const hasOneItemAndStackMedia = stackMediaAndCopy && cardCount === 1
  const itemsLength = hasFilters ? 3 : hasOneItemAndStackMedia ? 2 : cardCount
  const colSpan = stackMediaAndCopy ? Math.max(12 / Math.min(itemsLength ?? 1, 3), 3) : 12
  const hasHeadlineTitle = headline && headline?.title && (!isCarousel || (isCarouselMobileOnly && !isBelowSm))
  const shouldShowAsCarousel = (isCarousel && !isCarouselMobileOnly) || (isCarouselMobileOnly && isBelowSm)
  const shouldPeekNextSlide = shouldShowAsCarousel && isBelowMd

  const normalizedCards = useMemo(() => {
    return normalizeDataToCard(dataOptions || [], { openInNewTab: true })
  }, [dataOptions])

  // Use normalized cards when model selection is enabled, otherwise use regular cards (Manual filled)
  const displayCards = enableModelSelection ? normalizedCards : cards
  const displayCardCount = displayCards?.length ?? 0

  const calculateSlidesToShow = (slidesToShowDesktop: number, isStacked: boolean) => {
    if (!isStacked) return 1
    if (displayCardCount === 0) return 0

    // if there's only one card and desktop layout wants 3 or 4, show 2 instead
    if (displayCardCount === 1 && (slidesToShowDesktop === 3 || slidesToShowDesktop === 4)) {
      return 2
    }

    return Math.min(slidesToShowDesktop, displayCardCount)
  }

  const slidesToShowCalculated = calculateSlidesToShow(slidesToShow, stackMediaAndCopy)

  const responsive = getResponsiveCarouselSettings(slidesToShowCalculated, {
    sm: {
      dots: true
    }
  })

  const propsFromParent = {
    backgroundColor: backgroundColor,
    backgroundGradient: backgroundGradient,
    stackMediaAndCopy: stackMediaAndCopy || !!isBelowSm,
    centerCopyOnMobile: centerCopyOnMobile,
    hasDescriptionListCheckMark: hasDescriptionListCheckMark,
    hasLargeFontSizes: cardsHaveLargeFontSizes,
    subheadlineColor: subheadlineColor
  }

  return (
    <div className={cx(styles['card-group'], shouldPeekNextSlide && styles['card-group--peek-next-carousel-slide'])}>
      {hasHeadlineTitle && (
        <div className={styles[`card-group__headline`]}>
          <Heading {...headline} noMarginBottom noMarginTop>
            {headline.title}
          </Heading>
        </div>
      )}
      <Row
        style={{
          ...(shouldShowAsCarousel && {
            marginRight: 0,
            marginLeft: 0,
            display: 'block'
          }),
          rowGap: isBelowSm ? '24px' : '32px'
        }}
      >
        {shouldShowAsCarousel ? (
          <CarouselV2
            slides={displayCards ?? []}
            showArrowsBreakpointUp={null}
            headline={headline}
            peekNextSlide={!!shouldPeekNextSlide}
            carouselSettings={{
              variableWidth: false,
              slidesToShow: slidesToShowCalculated,
              responsive
            }}
            renderSlide={slide => <Card key={slide.id} {...slide} {...propsFromParent} />}
          />
        ) : (
          <>
            {displayCards && displayCards.length > 0 && (
              <>
                {displayCards.map(card => (
                  <Col
                    key={card.id}
                    md={Math.floor(colSpan)}
                    style={{
                      ...(hasOneItemAndStackMedia &&
                        !hasFilters && {
                          marginRight: 'auto',
                          marginLeft: 'auto'
                        })
                    }}
                  >
                    <Card {...card} {...propsFromParent} />
                  </Col>
                ))}
              </>
            )}
          </>
        )}
      </Row>
    </div>
  )
}

export default CardGroup
